/**
 * FAQ component - Frequently asked questions
 */

import {useState} from 'react';
import {Container} from '../ui/Container';
import {Section} from '../ui/Section';
import {SectionHeader} from '../ui/SectionHeader';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqItems: FAQItem[] = [
  {
    category: 'Billing',
    question: 'How does billing work?',
    answer: 'Billing is based on transcription hours used per month. Each plan includes a monthly allowance, and you can upgrade or add additional hours as needed.',
  },
  {
    category: 'Usage',
    question: 'Can I record meetings automatically?',
    answer: 'Yes, you can set up automatic recording for recurring meetings or specific meeting types. VoxaPilot integrates with Zoom, Google Meet, and Microsoft Teams.',
  },
  {
    category: 'Privacy',
    question: 'Is my data secure?',
    answer: 'Yes, we use end-to-end encryption and are SOC 2 certified. Your meeting data is encrypted in transit and at rest, and we never share your data with third parties.',
  },
  {
    category: 'Integrations',
    question: 'What integrations are available?',
    answer: 'VoxaPilot integrates with 50+ platforms including Zoom, Google Meet, Microsoft Teams, Slack, Salesforce, HubSpot, Notion, and more. See our integrations page for the full list.',
  },
  {
    category: 'Features',
    question: 'How accurate is the transcription?',
    answer: 'Our AI transcription achieves 99% accuracy for clear audio. Accuracy may vary with background noise, accents, or poor audio quality.',
  },
  {
    category: 'Enterprise',
    question: 'Do you offer enterprise plans?',
    answer: 'Yes, we offer custom enterprise plans with advanced features like SSO, custom retention policies, dedicated support, and API access. Contact our sales team for more information.',
  },
];

const categories = ['All', 'Billing', 'Usage', 'Privacy', 'Integrations', 'Features', 'Enterprise'];

export function FAQ() {
  const {ref, isVisible} = useScrollAnimation();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const filteredItems =
    selectedCategory === 'All'
      ? faqItems
      : faqItems.filter((item) => item.category === selectedCategory);

  return (
    <Section id="faq" padding="lg" background="gray" ref={ref}>
      <Container>
        <div className={`transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
        <SectionHeader
          title="Frequently Asked Questions"
          subtitle="Everything you need to know about VoxaPilot"
          centered
        />

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
                className={`
                px-4 py-2 rounded-md text-sm font-medium transition-colors
                ${
                  selectedCategory === category
                    ? 'bg-gray-900 dark:bg-[#1E1E1E] text-white'
                    : 'bg-white dark:bg-[#2D2D30] text-textSecondary dark:text-[#858585] hover:bg-gray-50 dark:hover:bg-[#3C3C3C] border border-gray-200 dark:border-gray-700'
                }
              `}
            >
              {category}
            </button>
          ))}
        </div>

        {/* FAQ Items */}
        <div className="max-w-3xl mx-auto space-y-5">
          {filteredItems.map((item, index) => (
            <div
              key={index}
              className="bg-background dark:bg-[#252526] border border-border dark:border-gray-700 rounded-lg overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-8 py-5 text-left flex items-center justify-between hover:bg-backgroundSecondary dark:hover:bg-[#2D2D30] transition-all duration-200 group"
              >
                <span className="font-semibold text-lg text-textPrimary dark:text-[#CCCCCC] group-hover:text-orange-600 dark:group-hover:text-orange-500 transition-colors">
                  {item.question}
                </span>
                <span className={`text-xl text-textSecondary dark:text-[#858585] transition-transform duration-300 ${
                  openIndex === index ? 'rotate-180' : ''
                }`}>
                  {openIndex === index ? '−' : '+'}
                </span>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-8 pb-6 text-base text-textSecondary dark:text-[#858585] leading-relaxed">
                  {item.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
        </div>
      </Container>
    </Section>
  );
}

